// NewSkuDialog — manually add a SKU for a special-order or one-off
// product that isn't in the standard catalog. Owner-only because it
// touches cost basis. Modeled on ReceiveDeliveryDialog.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  SkuCreateInput,
  SkuCategory,
  SkuUnit,
} from '@gutterinv/shared';
import { CANONICAL_UOMS, COUNTABLE_UNITS, displayUnit } from '@gutterinv/shared';
import { uomDefaultsForCategory } from '../lib/uom-defaults.js';
import { getSkuCategories, postSku, getSkuSpecOptions } from '../api/client.js';
import {
  specFieldsForCategory,
  SPEC_FIELD_META,
  type SpecFieldKey,
} from '../lib/sku-spec-fields.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';
import { qk } from '../lib/queryKeys.js';

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export function NewSkuDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data: categoriesRaw } = useQuery({
    queryKey: qk.skus.categories(),
    queryFn: getSkuCategories,
  });
  // Alphabetical by name for the dropdown (placeholder stays pinned first in the JSX).
  const categories = useMemo(
    () => (categoriesRaw ? [...categoriesRaw].sort((a, b) => a.name.localeCompare(b.name)) : categoriesRaw),
    [categoriesRaw],
  );

  const [categoryId, setCategoryId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [variant, setVariant] = useState('');
  // UoM layer: Buy (purchaseUom) / Sell (consumptionUom) / Count in (base unit) + Factor.
  const [buy, setBuy] = useState('EA');
  const [sell, setSell] = useState('EA');
  const [countIn, setCountIn] = useState<SkuUnit>('ea');
  const [factor, setFactor] = useState('1');
  const [costDollars, setCostDollars] = useState('');
  const [onHand, setOnHand] = useState('0');
  const [reorderThreshold, setReorderThreshold] = useState('0');
  // Category-specific spec values (keyed by generic field name) + universal Part #.
  // specs MUST be reset on category change (generic columns are shared across
  // categories — stale values would write onto a different category's row).
  const [specs, setSpecs] = useState<Record<string, string>>({});
  const [partNo, setPartNo] = useState('');

  // Apply a category's recommended Buy/Sell/Count-in/Factor. Factor defaults to 1 when
  // Buy === Sell (no conversion); when they differ it's blanked so the user must supply
  // the real per-SKU factor.
  const applyCategoryDefaults = useCallback((catName: string) => {
    const d = uomDefaultsForCategory(catName);
    setBuy(d.buy);
    setSell(d.sell);
    setCountIn(d.base);
    setFactor(d.buy === d.sell ? '1' : '');
  }, []);

  useEffect(() => {
    if (!open) return;
    const first = categories?.[0];
    setCategoryId(first?.id ?? '');
    setCode('');
    setName('');
    setVariant('');
    applyCategoryDefaults(first?.name ?? '');
    setCostDollars('');
    setOnHand('0');
    setReorderThreshold('0');
    setSpecs({});
    setPartNo('');
  }, [open, categories, applyCategoryDefaults]);

  // Changing category re-applies all three unit defaults (+ factor) for the new category.
  const onCategoryChange = (id: string) => {
    setCategoryId(id);
    const cat = categories?.find((c) => c.id === id);
    applyCategoryDefaults(cat?.name ?? '');
    // Reset spec values — generic columns are shared across categories, so a leftover
    // value from the previous category must NOT carry into the new one.
    setSpecs({});
  };

  // Spec fields for the selected category + the distinct values that seed them.
  const categoryName = useMemo(
    () => categories?.find((c) => c.id === categoryId)?.name,
    [categories, categoryId],
  );
  const specFields = useMemo(() => specFieldsForCategory(categoryName), [categoryName]);
  const { data: specOptions } = useQuery({
    queryKey: qk.skus.specOptions(categoryId),
    queryFn: () => getSkuSpecOptions(categoryId),
    enabled: open && !!categoryId && specFields.length > 0,
  });

  const mutation = useMutation({
    mutationFn: (input: SkuCreateInput) => postSku(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.inventory.skus() });
      void queryClient.invalidateQueries({ queryKey: qk.skus.all() });
      onOpenChange(false);
    },
  });

  // Factor is required (>0) when Buy and Sell differ; when equal it defaults to 1.
  const buySellDiffer = buy !== sell;
  const resolvedFactor = factor.trim() === '' ? (buySellDiffer ? NaN : 1) : Number(factor);
  const factorValid = Number.isFinite(resolvedFactor) && resolvedFactor > 0;

  const recommended = (() => {
    const cat = categories?.find((c) => c.id === categoryId);
    if (!cat) return null;
    const d = uomDefaultsForCategory(cat.name);
    return `Recommended for ${cat.name}: Buy ${d.buy} · Sell ${d.sell} · Count ${displayUnit(d.base)}`;
  })();

  const handleSave = () => {
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    if (!categoryId || !trimmedCode || !trimmedName) return;
    const cost = Number(costDollars);
    if (Number.isNaN(cost) || cost < 0) return;
    const onHandNum = Number(onHand);
    if (Number.isNaN(onHandNum) || onHandNum < 0) return;
    const reorderNum = Number(reorderThreshold);
    if (Number.isNaN(reorderNum) || reorderNum < 0) return;
    if (!factorValid) return;
    // Only the SELECTED category's fields go in the payload (never all 21 → no
    // cross-category column writes). Empty → null; numeric blank → null (never
    // Number('')===0). An invalid numeric blocks the save.
    const specPayload: Record<string, string | number | null> = {};
    for (const f of specFields) {
      const raw = (specs[f] ?? '').trim();
      if (!raw) {
        specPayload[f] = null;
        continue;
      }
      if (SPEC_FIELD_META[f].valueType === 'number') {
        const n = Number(raw);
        if (Number.isNaN(n) || n < 0) return;
        specPayload[f] = n;
      } else {
        specPayload[f] = raw;
      }
    }
    const payload: SkuCreateInput = {
      categoryId,
      code: trimmedCode,
      name: trimmedName,
      variant: variant.trim() || null,
      unit: countIn,
      purchaseUom: buy,
      consumptionUom: sell,
      conversionFactor: resolvedFactor,
      costCents: Math.round(cost * 100),
      onHand: onHandNum,
      reorderThreshold: reorderNum,
      trackingLevel: 'loose',
      partNo: partNo.trim() || null,
      ...(specPayload as Partial<SkuCreateInput>),
    };
    mutation.mutate(payload);
  };

  const canSave =
    !!categoryId && !!code.trim() && !!name.trim() && costDollars !== '' && factorValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>New SKU</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-2 pr-5">
          {/* Category */}
          <div>
            <label htmlFor="sku-category" className="eyebrow mb-1.5 block">
              Category *
            </label>
            <select
              id="sku-category"
              value={categoryId}
              onChange={(e) => onCategoryChange(e.target.value)}
              disabled={mutation.isPending}
              className={selectClass}
            >
              <option value="">Choose a category…</option>
              {categories?.map((c: SkuCategory) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.defaultMarkupPct}% markup
                </option>
              ))}
            </select>
          </div>

          {/* Code */}
          <div>
            <label htmlFor="sku-code" className="eyebrow mb-1.5 block">
              SKU code *
            </label>
            <Input
              id="sku-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. SPECIAL-001"
              disabled={mutation.isPending}
            />
          </div>

          {/* Units: Buy (purchase) · Sell (consume) · Count in (base) · Factor */}
          <div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label htmlFor="sku-buy" className="eyebrow mb-1.5 block">
                  Buy *
                </label>
                <select
                  id="sku-buy"
                  value={buy}
                  onChange={(e) => setBuy(e.target.value)}
                  disabled={mutation.isPending}
                  className={selectClass}
                >
                  {CANONICAL_UOMS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sku-sell" className="eyebrow mb-1.5 block">
                  Sell *
                </label>
                <select
                  id="sku-sell"
                  value={sell}
                  onChange={(e) => setSell(e.target.value)}
                  disabled={mutation.isPending}
                  className={selectClass}
                >
                  {CANONICAL_UOMS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sku-countin" className="eyebrow mb-1.5 block">
                  Count in *
                </label>
                <select
                  id="sku-countin"
                  value={countIn}
                  onChange={(e) => setCountIn(e.target.value as SkuUnit)}
                  disabled={mutation.isPending}
                  className={selectClass}
                >
                  {COUNTABLE_UNITS.map((u) => (
                    <option key={u} value={u}>{displayUnit(u)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sku-factor" className="eyebrow mb-1.5 block">
                  Factor{buySellDiffer ? ' *' : ''}
                </label>
                <Input
                  id="sku-factor"
                  type="number"
                  min="0"
                  step="any"
                  value={factor}
                  onChange={(e) => setFactor(e.target.value)}
                  placeholder={buySellDiffer ? 'Sell per Buy' : '1'}
                  disabled={mutation.isPending}
                  className={!factorValid ? 'border-destructive' : undefined}
                />
              </div>
            </div>
            {recommended && (
              <p className="mt-1.5 text-xs text-muted-foreground">{recommended}</p>
            )}
            {buySellDiffer && !factorValid && (
              <p className="mt-1 text-xs text-destructive">
                Buy and Sell differ — enter the conversion factor (Sell units per 1 Buy unit).
              </p>
            )}
          </div>

          {/* Name */}
          <div>
            <label htmlFor="sku-name" className="eyebrow mb-1.5 block">
              Name *
            </label>
            <Input
              id="sku-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Custom copper downspout"
              disabled={mutation.isPending}
            />
          </div>

          {/* Variant */}
          <div>
            <label htmlFor="sku-variant" className="eyebrow mb-1.5 block">
              Variant
            </label>
            <Input
              id="sku-variant"
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              placeholder="Color, size, or other detail (optional)"
              disabled={mutation.isPending}
            />
          </div>

          {/* Cost + onHand + reorder */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="sku-cost" className="eyebrow mb-1.5 block">
                Cost ($) *
              </label>
              <Input
                id="sku-cost"
                type="number"
                min="0"
                step="0.01"
                value={costDollars}
                onChange={(e) => setCostDollars(e.target.value)}
                placeholder="per unit"
                disabled={mutation.isPending}
              />
            </div>
            <div>
              <label htmlFor="sku-onhand" className="eyebrow mb-1.5 block">
                On hand
              </label>
              <Input
                id="sku-onhand"
                type="number"
                min="0"
                step="any"
                value={onHand}
                onChange={(e) => setOnHand(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>
            <div>
              <label htmlFor="sku-reorder" className="eyebrow mb-1.5 block">
                Reorder at
              </label>
              <Input
                id="sku-reorder"
                type="number"
                min="0"
                step="any"
                value={reorderThreshold}
                onChange={(e) => setReorderThreshold(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>
          </div>

          {/* Specifications — category-aware. Seeded from existing values in THIS
              category; comboboxes accept new typed values. All optional. */}
          {specFields.length > 0 && (
            <div>
              <div className="eyebrow mb-1.5">Specifications</div>
              <div className="grid grid-cols-2 gap-3">
                {specFields.map((f) => (
                  <div key={f}>
                    <label
                      htmlFor={`sku-spec-${f}`}
                      className="mb-1.5 block text-xs text-muted-foreground"
                    >
                      {SPEC_FIELD_META[f].label}
                    </label>
                    <SpecFieldControl
                      field={f}
                      value={specs[f] ?? ''}
                      options={specOptions?.[f] ?? []}
                      disabled={mutation.isPending}
                      onChange={(v) => setSpecs((s) => ({ ...s, [f]: v }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Part # — universal (rendered for every category) */}
          <div>
            <label htmlFor="sku-partno" className="eyebrow mb-1.5 block">
              Part #
            </label>
            <Input
              id="sku-partno"
              value={partNo}
              onChange={(e) => setPartNo(e.target.value)}
              placeholder="Vendor / UPC part number (optional)"
              disabled={mutation.isPending}
            />
          </div>

          {mutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending || !canSave}>
            {mutation.isPending ? 'Saving…' : 'Create SKU'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// One spec control, driven by the field's control type. select → pick existing only;
// combobox → input + datalist (pick existing OR type a NEW value); numeric → number input.
function SpecFieldControl({
  field,
  value,
  options,
  disabled,
  onChange,
}: {
  field: SpecFieldKey;
  value: string;
  options: string[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const meta = SPEC_FIELD_META[field];
  const id = `sku-spec-${field}`;
  if (meta.control === 'select') {
    return (
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={selectClass}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (meta.control === 'combobox') {
    const listId = `${id}-list`;
    return (
      <>
        <Input
          id={id}
          list={listId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Select or type…"
          disabled={disabled}
        />
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      </>
    );
  }
  return (
    <Input
      id={id}
      type="number"
      min="0"
      step="any"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  );
}
